import Iconify from "@/components/ui/Iconify";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { placeTypes } from "@/data/place-types";
import { placeSubcategories } from "@/data/place-subcategory";
import { useCallback, useEffect, useRef, useState } from "react";
import Fuse from "fuse.js";
import Highlighter from "react-highlight-words";
import { enqueueSnackbar } from "notistack";
import { createAddPlaceSuggestion } from "@/api/places";
import { useTranslations } from "@/lib/static-intl";
import MapLibreLocationPicker from "@/components/maplibre-location-picker";

const fuseOptions = {
  includeMatches: true,
  threshold: 0.4,
};

const fallbackLocation = {
  latitude: 37.54666563187861,
  longitude: 126.98225129089356, // Seoul
};

const placeTypeFuse = new Fuse(placeTypes || [], fuseOptions);
const placeSubcategoriesFuse = new Fuse(placeSubcategories || [], fuseOptions);

export const PlaceAddModal = ({ isOpen, onClose }) => {
  const placesT = useTranslations("places_add_modal");

  const { user } = useAuthContext();
  const [showMoreDetailsForm, setShowMoreDetailsForm] = useState(false);

  const categoryRef = useRef(null);
  const [isCategoryInpInFocus, setIsCategoryInpInFocus] = useState(false);

  const subCategoryRef = useRef(null);
  const [isSubCategoryInpInFocus, setIsSubCategoryInpInFocus] = useState(false);

  const [formLoader, setFormLoader] = useState(false);
  const [mapCenter, setMapCenter] = useState({
    lat: fallbackLocation.latitude,
    lng: fallbackLocation.longitude,
  });
  const [isMapInteractive, setIsMapInteractive] = useState(false);

  const [formDetails, setFormDetails] = useState({
    name: "",
    type: "",
    subcategory: "",
    address: "",
    old_address: "",
    businessHours: "",
    phoneNumber: "",
    website: "",
    amenities: "",
    seating: "",
    addressInformation: "",
    parking: "",
    introduction: "",
    placeMedia: [],
    latitude: fallbackLocation.latitude,
    longitude: fallbackLocation.longitude,
  });

  const filteredPlaceTypes =
    formDetails?.type?.trim()?.length > 0
      ? placeTypeFuse.search(formDetails.type)
      : placeTypes?.map((type) => ({ item: type }));

  const filteredPlaceSubcategories =
    formDetails?.subcategory?.trim()?.length > 0
      ? placeSubcategoriesFuse.search(formDetails.subcategory)
      : placeSubcategories?.map((subcategory) => ({ item: subcategory }));

  const toggleShowMoreDetailsForm = () => {
    setShowMoreDetailsForm((prev) => !prev);
  };

  const handleAddPhotosClick = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = false;

    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      setFormDetails((prev) => ({
        ...prev,
        placeMedia: [...files, ...(prev?.placeMedia || [])],
      }));
    };

    input.click();
  };

  const handleRemoveMedia = (index) => {
    setFormDetails((prev) => ({
      ...prev,
      placeMedia: prev?.placeMedia?.filter((_, i) => i !== index),
    }));
  };

  const resetForm = () => {
    setFormDetails({
      name: "",
      type: "",
      subcategory: "",
      address: "",
      old_address: "",
      businessHours: "",
      phoneNumber: "",
      website: "",
      amenities: "",
      seating: "",
      addressInformation: "",
      parking: "",
      introduction: "",
      placeMedia: [],
      latitude: null,
      longitude: null,
    });
  };

  const handleFormSubmit = async () => {
    try {
      const {
        name,
        type,
        subcategory,
        address,
        old_address,
        latitude,
        longitude,
        amenities,
        seating,
        addressInformation,
        parking,
        introduction,
        phoneNumber,
        website,
        businessHours,
      } = formDetails;

      if (!name.trim()) {
        enqueueSnackbar(placesT("place_name_is_req"), { variant: "error" });
        return;
      }
      if (!type.trim()) {
        enqueueSnackbar(placesT("category_is_req"), { variant: "error" });
        return;
      }
      if (!subcategory.trim()) {
        enqueueSnackbar(placesT("subcategory_is_req"), { variant: "error" });
        return;
      }
      if (!address.trim()) {
        enqueueSnackbar(placesT("address_is_req"), { variant: "error" });
        return;
      }
      if (!old_address.trim()) {
        enqueueSnackbar(placesT("old_address_is_req"), { variant: "error" });
        return;
      }
      if (latitude === null || longitude === null) {
        enqueueSnackbar(placesT("location_is_req"), {
          variant: "error",
        });
        return;
      }

      console.log("formDetails -->", formDetails);
      setFormLoader(true);
      const formData = new FormData();
      formDetails?.placeMedia?.forEach((file) => {
        formData.append("placeMedia", file);
      });
      formData.append("name", name);
      formData.append("type", type);
      formData.append("subcategory", subcategory);
      formData.append("address", address);
      formData.append("old_address", old_address);
      formData.append("userId", user?.userId);
      formData.append("amenities", amenities);
      formData.append("phoneNumber", phoneNumber);
      formData.append("businessHours", businessHours);
      formData.append("website", website);
      formData.append("seating", seating);
      formData.append("addressInformation", addressInformation);
      formData.append("parking", parking);
      formData.append("introduction", introduction);
      formData.append("latitude", latitude);
      formData.append("longitude", longitude);

      const response = await createAddPlaceSuggestion(formData);
      console.log("response -->", response);
      // Proceed with API call or next steps
      if (response?.id) {
        enqueueSnackbar(placesT("place_added_successfully"), {
          variant: "success",
        });
        resetForm();
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong",
        { variant: "error" }
      );
    } finally {
      setFormLoader(false);
    }
  };

  const handleMapCenterChange = useCallback((center) => {
    const lat = Number(center?.lat);
    const lng = Number(center?.lng);

    if (Number.isFinite(lat) && Number.isFinite(lng)) setMapCenter({ lat, lng });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        categoryRef?.current &&
        !categoryRef?.current.contains(event.target)
      ) {
        setIsCategoryInpInFocus(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        subCategoryRef?.current &&
        !subCategoryRef?.current.contains(event.target)
      ) {
        setIsSubCategoryInpInFocus(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      className="fixed top-0 left-0 right-0 bottom-0 w-full h-screen overflow-hidden flex items-center justify-center z-[99] bg-black/60 backdrop-blur"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-screen h-screen md:max-h-[650px] md:w-full md:max-w-2xl bg-white dark:bg-custom_black border dark:border-brown md:rounded-3xl overflow-hidden flex flex-col pb-12 md:pb-0"
      >
        <div className="w-full px-5 py-3 border-b dark:border-brown flex items-center justify-between">
          <span className="text-start text-t-title font-medium">
            {placesT("add_a_place")}
          </span>
          <button type="button" onClick={onClose}>
            <Iconify
              icon={"material-symbols:close-rounded"}
              className={"!min-w-6 !w-6 !h-6"}
            />
          </button>
        </div>
        <div className="w-full h-full overflow-y-auto overflow-x-hidden thin-scrollbar flex flex-col gap-5 py-3">
          <div className="w-full flex flex-col gap-0 px-5">
            <span className="font-semibold">{placesT("place_details")}</span>
            <span className="text-t-sub text-black/80">
              {placesT("place_details_subtext")}
            </span>
          </div>

          <div className="w-full flex flex-col gap-1 px-5">
            <span className="text-t-sub font-semibold">
              {placesT("place_nam_req")}
            </span>
            <input
              type="text"
              placeholder={placesT("place_name")}
              name="name"
              value={formDetails?.name}
              onChange={(e) =>
                setFormDetails({
                  ...formDetails,
                  [e.target.name]: e.target.value,
                })
              }
              className="w-full rounded-chip border px-3 py-3 focus:outline-none focus:border-ink dark:focus:border-ink_on_dark transition dark:border-brown"
            />
          </div>

          <div
            ref={categoryRef}
            className="w-full flex flex-col gap-1 px-5 relative"
          >
            <span className="text-t-sub font-semibold">
              {placesT("category_req")}
            </span>
            <input
              type="text"
              placeholder={placesT("category")}
              name="type"
              value={formDetails?.type}
              onChange={(e) =>
                setFormDetails({
                  ...formDetails,
                  [e.target.name]: e.target.value,
                })
              }
              onFocus={() => setIsCategoryInpInFocus(true)}
              className="w-full rounded-chip border px-3 py-3 focus:outline-none focus:border-ink dark:focus:border-ink_on_dark transition dark:border-brown"
            />
            {isCategoryInpInFocus && filteredPlaceTypes?.length > 0 && (
              <div className="absolute top-2 translate-y-16 right-5 left-5 max-h-[250px] bg-white dark:bg-brown rounded-chip border dark:border-brown flex flex-col divide-y dark:divide-brown overflow-y-auto overflow-x-hidden thin-scrollbar z-20">
                {filteredPlaceTypes?.map((result, i) => {
                  const placeType = result?.item || result; // fallback for non-fuse case

                  return (
                    <button
                      key={placeType + i}
                      type="button"
                      className="w-full px-4 py-4 text-start outline-none border-none hover:bg-black/5 dark:hover:bg-white/10 leading-tight tracking-tight"
                      onClick={() => {
                        setFormDetails({
                          ...formDetails,
                          type: placeType,
                        });
                        setIsCategoryInpInFocus(false);
                      }}
                    >
                      <Highlighter
                        highlightClassName="text-red bg-transparent font-bold"
                        searchWords={[formDetails?.type]}
                        autoEscape={true}
                        textToHighlight={placeType}
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div
            ref={subCategoryRef}
            className="w-full flex flex-col gap-1 px-5 relative"
          >
            <span className="text-t-sub font-semibold">
              {placesT("sub_category_req")}
            </span>
            <input
              type="text"
              placeholder={placesT("sub_category")}
              name="subcategory"
              value={formDetails?.subcategory}
              onChange={(e) =>
                setFormDetails({
                  ...formDetails,
                  [e.target.name]: e.target.value,
                })
              }
              onFocus={() => setIsSubCategoryInpInFocus(true)}
              className="w-full rounded-chip border px-3 py-3 focus:outline-none focus:border-ink dark:focus:border-ink_on_dark transition dark:border-brown"
            />
            {isSubCategoryInpInFocus &&
              filteredPlaceSubcategories?.length > 0 && (
                <div className="absolute top-2 translate-y-16 right-5 left-5 max-h-[250px] bg-white dark:bg-brown rounded-chip border dark:border-brown flex flex-col divide-y dark:divide-brown overflow-y-auto overflow-x-hidden thin-scrollbar z-20">
                  {filteredPlaceSubcategories?.map((result, i) => {
                    const placeSubcategory = result?.item || result; // fallback for non-fuse case

                    return (
                      <button
                        key={placeSubcategory + i}
                        type="button"
                        className="w-full px-4 py-4 text-start outline-none border-none hover:bg-black/5 dark:hover:bg-white/5 leading-tight tracking-tight"
                        onClick={() => {
                          setFormDetails({
                            ...formDetails,
                            subcategory: placeSubcategory,
                          });
                          setIsSubCategoryInpInFocus(false);
                        }}
                      >
                        <Highlighter
                          highlightClassName="text-red bg-transparent font-bold"
                          searchWords={[formDetails?.subcategory]}
                          autoEscape={true}
                          textToHighlight={placeSubcategory}
                        />
                      </button>
                    );
                  })}
                </div>
              )}
          </div>

          <div className="w-full flex flex-col gap-1 px-5">
            <span className="text-t-sub font-semibold">
              {placesT("address_req")}
            </span>
            <input
              type="text"
              placeholder={placesT("address")}
              name="address"
              value={formDetails?.address}
              onChange={(e) =>
                setFormDetails({
                  ...formDetails,
                  [e.target.name]: e.target.value,
                })
              }
              className="w-full rounded-chip border dark:border-brown px-3 py-3 focus:outline-none focus:border-ink dark:focus:border-ink_on_dark transition"
            />
          </div>

          <div className="w-full flex flex-col gap-1 px-5">
            <span className="text-t-sub font-semibold">
              {placesT("old_address_req")}
            </span>
            <input
              type="text"
              placeholder={placesT("old_address")}
              name="old_address"
              value={formDetails?.old_address}
              onChange={(e) =>
                setFormDetails({
                  ...formDetails,
                  [e.target.name]: e.target.value,
                })
              }
              className="w-full rounded-chip border dark:border-brown px-3 py-3 focus:outline-none focus:border-ink dark:focus:border-ink_on_dark transition"
            />
          </div>

          <div
            key={`map-container-${isMapInteractive}`}
            className="relative px-5 h-[250px] min-h-[250px] w-full"
          >
            <MapLibreLocationPicker
              center={mapCenter}
              zoom={18}
              isInteractive={isMapInteractive}
              onChange={handleMapCenterChange}
            />
            <button
              type="button"
              onClick={() => {
                setFormDetails({
                  ...formDetails,
                  latitude: mapCenter?.lat,
                  longitude: mapCenter?.lng,
                });
                setIsMapInteractive((prev) => !prev);
              }}
              className="absolute bottom-3 left-8 z-40 bg-white rounded-full px-5 py-2 shadow-lg text-t-sub border dark:text-black"
            >
              {isMapInteractive ? "Save this location" : "Edit place location"}
            </button>
          </div>

          {showMoreDetailsForm ? (
            <>
              <div className="w-full flex flex-col gap-1 px-5">
                <span className="text-t-sub font-semibold">
                  {placesT("business_hours")}
                </span>
                <textarea
                  rows={5}
                  placeholder={placesT("business_hours")}
                  name="businessHours"
                  value={formDetails?.businessHours}
                  onChange={(e) =>
                    setFormDetails({
                      ...formDetails,
                      [e.target.name]: e.target.value,
                    })
                  }
                  className="w-full rounded-chip border dark:border-brown px-3 py-3 focus:outline-none focus:border-ink dark:focus:border-ink_on_dark transition resize-none"
                />
              </div>

              <div className="w-full flex flex-col gap-1 px-5">
                <span className="text-t-sub font-semibold">
                  {placesT("introduction")}
                </span>
                <textarea
                  rows={5}
                  placeholder={placesT("introduction")}
                  name="introduction"
                  value={formDetails?.introduction}
                  onChange={(e) =>
                    setFormDetails({
                      ...formDetails,
                      [e.target.name]: e.target.value,
                    })
                  }
                  className="w-full rounded-chip border dark:border-brown px-3 py-3 focus:outline-none focus:border-ink dark:focus:border-ink_on_dark transition resize-none"
                />
              </div>

              <div className="w-full flex flex-col gap-1 px-5">
                <span className="text-t-sub font-semibold">
                  {placesT("amenities")}
                </span>
                <textarea
                  rows={5}
                  placeholder={placesT("amenities")}
                  name="amenities"
                  value={formDetails?.amenities}
                  onChange={(e) =>
                    setFormDetails({
                      ...formDetails,
                      [e.target.name]: e.target.value,
                    })
                  }
                  className="w-full rounded-chip border dark:border-brown px-3 py-3 focus:outline-none focus:border-ink dark:focus:border-ink_on_dark transition resize-none"
                />
              </div>

              <div className="w-full flex flex-col gap-1 px-5">
                <span className="text-t-sub font-semibold">
                  {placesT("seating")}
                </span>
                <textarea
                  rows={5}
                  placeholder={placesT("seating")}
                  name="seating"
                  value={formDetails?.seating}
                  onChange={(e) =>
                    setFormDetails({
                      ...formDetails,
                      [e.target.name]: e.target.value,
                    })
                  }
                  className="w-full rounded-chip border dark:border-brown px-3 py-3 focus:outline-none focus:border-ink dark:focus:border-ink_on_dark transition resize-none"
                />
              </div>

              <div className="w-full flex flex-col gap-1 px-5">
                <span className="text-t-sub font-semibold">
                  {placesT("address_info")}
                </span>
                <textarea
                  rows={5}
                  placeholder={placesT("address_info")}
                  name="addressInformation"
                  value={formDetails?.addressInformation}
                  onChange={(e) =>
                    setFormDetails({
                      ...formDetails,
                      [e.target.name]: e.target.value,
                    })
                  }
                  className="w-full rounded-chip border dark:border-brown px-3 py-3 focus:outline-none focus:border-ink dark:focus:border-ink_on_dark transition resize-none"
                />
              </div>

              <div className="w-full flex flex-col gap-1 px-5">
                <span className="text-t-sub font-semibold">
                  {placesT("parking")}
                </span>
                <textarea
                  rows={5}
                  placeholder={placesT("parking")}
                  name="parking"
                  value={formDetails?.parking}
                  onChange={(e) =>
                    setFormDetails({
                      ...formDetails,
                      [e.target.name]: e.target.value,
                    })
                  }
                  className="w-full rounded-chip border dark:border-brown px-3 py-3 focus:outline-none focus:border-ink dark:focus:border-ink_on_dark transition resize-none"
                />
              </div>

              <div className="w-full flex flex-col gap-1 px-5">
                <span className="text-t-sub font-semibold">
                  {placesT("contact")}
                </span>
                <input
                  type="text"
                  placeholder={placesT("contact")}
                  name="phoneNumber"
                  value={formDetails?.phoneNumber}
                  onChange={(e) =>
                    setFormDetails({
                      ...formDetails,
                      [e.target.name]: e.target.value,
                    })
                  }
                  className="w-full rounded-chip border dark:border-brown px-3 py-3 focus:outline-none focus:border-ink dark:focus:border-ink_on_dark transition"
                />
              </div>

              <div className="w-full flex flex-col gap-1 px-5">
                <input
                  type="text"
                  placeholder={placesT("website")}
                  name="website"
                  value={formDetails?.website}
                  onChange={(e) =>
                    setFormDetails({
                      ...formDetails,
                      [e.target.name]: e.target.value,
                    })
                  }
                  className="w-full rounded-chip border dark:border-brown px-3 py-3 focus:outline-none focus:border-ink dark:focus:border-ink_on_dark transition"
                />
              </div>

              <div className="w-full flex flex-col gap-1 px-5">
                <span className="font-semibold">{placesT("place_photos")}</span>
                <span className="text-t-sub text-black/60 dark:text-white/50 font-medium">
                  {placesT("places_photos_subtext")}
                </span>
                <span className="text-t-label">{placesT("posting_publicly")}</span>
                <span className="text-t-label">{user?.username}</span>
                {formDetails?.placeMedia?.length > 0 ? (
                  <div className="w-full flex overflow-x-auto thin-scrollbar gap-4 mt-2 pb-2">
                    <button
                      type="button"
                      onClick={handleAddPhotosClick}
                      className="w-[150px] h-[150px] min-h-[150px] min-w-[150px] border dark:border-brown rounded-xl flex flex-col items-center justify-center opacity-50 hover:opacity-100 transition-opacity"
                    >
                      <Iconify
                        icon={"iconoir:add-media-image"}
                        className={"!text-red !min-w-8 !h-8"}
                      />
                    </button>
                    {formDetails?.placeMedia?.map((media, index) => (
                      <div key={index} className="w-fit h-fit relative group">
                        <img
                          src={URL.createObjectURL(media)}
                          alt="media"
                          className="w-[150px] h-[150px] border dark:border-brown rounded-xl max-h-[150px] overflow-hidden object-cover"
                        />
                        <div className="absolute top-0 bottom-0 left-0 right-0 z-20 hidden group-hover:flex flex-col items-end p-1.5">
                          <button
                            type="button"
                            onClick={() => handleRemoveMedia(index)}
                            className="p-1 rounded-chip flex items-center justify-center bg-black/30 backdrop-blur-xl"
                          >
                            <Iconify
                              icon={"material-symbols:close-rounded"}
                              className={"!text-white"}
                            />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleAddPhotosClick}
                    className="w-fit flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 border text-ink text-t-sub mt-2 font-medium dark:border-brown"
                  >
                    <Iconify
                      icon="pixelarticons:camera-add"
                      className="!text-inherit"
                    />
                    <span className="text-inherit">
                      {placesT("add_photos")}
                    </span>
                  </button>
                )}
              </div>
            </>
          ) : (
            <div
              onClick={toggleShowMoreDetailsForm}
              className="w-full px-5 py-5 flex flex-col gap-5 border-y dark:border-y-brown"
            >
              <div className="w-full flex gap-5">
                <div className="w-full flex flex-col gap-0">
                  <span className="font-semibold">
                    {placesT("add_more_details")}
                  </span>
                  <span className="text-black/80 dark:text-white/70">
                    {placesT("add_more_details_subtext")}
                  </span>
                </div>
                <div className="h-full flex items-start">
                  <button type="button" onClick={toggleShowMoreDetailsForm}>
                    <Iconify icon={"grommet-icons:down"} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="fixed bottom-0 left-0 right-0 z-[100] flex gap-3 md:gap-5 bg-white dark:bg-custom_black md:static md:flex w-full px-8 py-3 items-center justify-end border-t dark:border-brown">
          <button
            type="button"
            disabled={formLoader}
            onClick={handleFormSubmit}
            className="inline-flex w-fit items-center justify-center gap-1.5 rounded-full bg-action px-5 py-2.5 text-t-sub font-bold text-white transition hover:brightness-110 disabled:opacity-60 disabled:pointer-events-none"
          >
            {formLoader && (
              <Iconify
                icon={"eos-icons:loading"}
                className={"!text-inherit animate-spin"}
              />
            )}
            {placesT("submit")}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex w-fit items-center justify-center rounded-full px-4 py-2.5 text-t-sub font-bold text-ink-70 transition hover:text-ink dark:text-ink_on_dark-70 dark:hover:text-ink_on_dark"
          >
            {placesT("close")}
          </button>
        </div>
      </div>
    </div>
  );
};
